import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { GitBranch, Plus, Pen, Trash2, Search, MapPin, Phone, Building2, X as IconX, Star, Power, Boxes, Map as MapIcon, Warehouse, ShoppingCart, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  branchesOf, addBranch, updateBranch, deleteBranch, setBranchPurchaseEnabled,
  zonesOf, findZone, warehousesOf, warehousesOfZone, findWarehouse, isModuleOn,
} from '../../data/store';
import { SideDrawer } from '../../components/SideDrawer';
import { CustomSelect } from '../../components/CustomSelect';
import { formatCategoryName, formatPhone } from '../../utils/formatters';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

const blank = (orgId, defaultZoneId) => ({
  id: null,
  orgId,
  zoneId: defaultZoneId || null,
  name: '',
  address: '',
  phone: '',
  altPhone: '',
  status: 'Active',
  isMain: false,
  warehouseSourceIds: [],
  purchaseEnabled: true,
});

const Field = ({ label, icon: Icon, error, required, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
      {label} {required && <span className="text-rose-500 ml-1">*</span>}
    </span>
    <span className="relative block">
      {Icon && (
        <span className={`absolute left-3 top-1/2 -translate-y-1/2 z-30 pointer-events-none ${error ? 'text-rose-400' : 'text-slate-400'}`} aria-hidden="true">
          <Icon size={14} />
        </span>
      )}
      {React.cloneElement(children, {
        className: `w-full rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? '!pl-9' : ''} ${children.props.className || ''}`,
      })}
    </span>
    {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
  </label>
);

Field.propTypes = {
  label: PropTypes.node.isRequired,
  error: PropTypes.string,
  required: PropTypes.bool,
  children: PropTypes.element.isRequired,
};

const BranchModal = ({ open, draft, setDraft, onClose, onSubmit, mode, accent, zones, zoneEnabled, warehouseEnabled, showNotification }) => {
  const [errors, setErrors] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  // Clear validation errors whenever the modal opens so stale errors from a
  // previous failed save don't bleed into the next branch's edit form.
  useEffect(() => {
    if (open) {
      setErrors({});
      setCustomOpen(false);
    }
  }, [open]);
  const draftZoneId = draft?.zoneId;
  const draftOrgId = draft?.orgId;
  const draftSourceIds = draft?.warehouseSourceIds;
  const zoneWarehouses = useMemo(
    () => (draftZoneId ? warehousesOfZone(draftZoneId) : []),
    [draftZoneId],
  );
  // Every warehouse in the org except the ones already in this branch's zone.
  // Cross-border supply: e.g. a Pondicherry warehouse can feed a Tamil Nadu branch.
  const otherZoneWarehouses = useMemo(() => {
    if (!draftOrgId) return [];
    const inZone = new Set(zoneWarehouses.map((w) => w.id));
    return warehousesOf(draftOrgId).filter((w) => !inZone.has(w.id));
  }, [draftOrgId, zoneWarehouses]);
  // Group cross-zone warehouses by zone for the "Add custom" panel.
  // Filter out warehouses that are already in customSelected so they don't
  // appear twice (once as a selected chip, once in the picker).
  const otherByZone = useMemo(() => {
    const selectedIds = new Set(draftSourceIds || []);
    const map = new Map();
    otherZoneWarehouses
      .filter((w) => !selectedIds.has(w.id))
      .forEach((w) => {
        const z = findZone(w.zoneId);
        const key = z?.id || '__nozone__';
        if (!map.has(key)) map.set(key, { zone: z, warehouses: [] });
        map.get(key).warehouses.push(w);
      });
    return Array.from(map.values());
  }, [otherZoneWarehouses, draftSourceIds]);
  // Selected cross-zone warehouses (explicit out-of-zone picks).
  const customSelected = useMemo(() => {
    const sourceIds = draftSourceIds || [];
    const zoneIds = new Set(zoneWarehouses.map((w) => w.id));
    return sourceIds
      .filter((id) => !zoneIds.has(id))
      .map((id) => findWarehouse(id))
      .filter(Boolean);
  }, [draftSourceIds, zoneWarehouses]);

  if (!open) return null;

  const toggleWarehouse = (whId) => {
    const sources = draft.warehouseSourceIds || [];
    const next = sources.includes(whId)
      ? sources.filter((id) => id !== whId)
      : [...sources, whId];
    setDraft({ ...draft, warehouseSourceIds: next });
  };
  // Zone change keeps the user's selections — cross-zone picks are now
  // first-class so we no longer purge them. Org-admins can prune manually.
  const onZoneChange = (zoneId) => {
    setDraft({ ...draft, zoneId });
    if (errors.zoneId) setErrors({ ...errors, zoneId: undefined });
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Please fill the Branch name.';
    
    if (zoneEnabled && !draft.zoneId) {
      newErrors.zoneId = 'Please select a Zone.';
    }

    const phonePattern = /^\+?\d{10,14}$/;
    
    if (!draft.phone || !draft.phone.trim()) {
      newErrors.phone = 'Please provide a Mobile Number.';
    } else if (!phonePattern.test(draft.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Please provide a valid 10-digit mobile number.';
    }
    
    if (draft.altPhone && !phonePattern.test(draft.altPhone.replace(/[\s-]/g, ''))) {
      newErrors.altPhone = 'Please provide a valid 10-digit alt mobile number.';
    }

    if (!draft.address || !draft.address.trim()) {
      newErrors.address = 'Please provide an Address.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };

  return (
    <SideDrawer onClose={onClose}>
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><GitBranch size={18} /></div>
          <div>
            <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit branch' : 'New branch'}</h3>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Each branch owns one inventory</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
      </header>

      <div className="px-6 py-5 flex-1 overflow-y-auto custom-scrollbar grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Field label="Branch name" icon={Building2} error={errors.name} required>
            <input type="text" value={draft.name} onChange={(e) => {
              setDraft({ ...draft, name: formatCategoryName(e.target.value) });
              if (errors.name) setErrors({ ...errors, name: undefined });
            }} placeholder="e.g. Saaram" />
          </Field>
        </div>

        {zoneEnabled && (
          <div className="md:col-span-2">
            <Field label="Zone" icon={MapIcon} error={errors.zoneId} required>
              <CustomSelect
                value={draft.zoneId || ''}
                onChange={(v) => onZoneChange(v)}
                placeholder="Select a zone…"
                options={zones.map((z) => ({ value: z.id, label: z.name }))}
              />
            </Field>
          </div>
        )}


        <Field label="Mobile Number" icon={Phone} error={errors.phone} required>
          <input type="tel" value={draft.phone} onChange={(e) => {
            setDraft({ ...draft, phone: formatPhone(e.target.value) });
            if (errors.phone) setErrors({ ...errors, phone: undefined });
          }} placeholder="+91 9999999999" />
        </Field>
        <Field label="Alt Mobile Number" icon={Phone} error={errors.altPhone}>
          <input type="tel" value={draft.altPhone || ''} onChange={(e) => {
            setDraft({ ...draft, altPhone: formatPhone(e.target.value) });
            if (errors.altPhone) setErrors({ ...errors, altPhone: undefined });
          }} placeholder="+91 9999999999" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Address" icon={MapPin} error={errors.address} required>
            <input type="text" value={draft.address} onChange={(e) => {
              setDraft({ ...draft, address: e.target.value });
              if (errors.address) setErrors({ ...errors, address: undefined });
            }} placeholder="Street, city" />
          </Field>
        </div>
        <Field label="Status">
          <CustomSelect
            value={draft.status}
            onChange={(v) => setDraft({ ...draft, status: v })}
            options={[
              { value: 'Active', label: 'ACTIVE', icon: <Power size={14} className="text-emerald-500" /> },
              { value: 'Offline', label: 'OFFLINE', icon: <Power size={14} className="text-rose-500" /> }
            ]}
          />
        </Field>

        {warehouseEnabled && (
          <div 
            className="md:col-span-2 rounded-lg bg-white border border-slate-200 border-l-4 px-4 py-3 shadow-sm flex flex-col items-start justify-center gap-3"
            style={{ borderLeftColor: accent }}
          >
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-[#0d0d0d] inline-flex items-center gap-1.5">
                <ShoppingCart size={12} aria-hidden="true" /> Track purchases for this branch
              </p>
            </div>
            <div className="min-w-0 w-full flex items-center justify-between gap-2 mt-auto">
              <p className="text-[10px] font-medium text-slate-500 leading-snug">When on, this branch records its own Inventory_Purchase on top of warehouse-sourced stock.</p>
              <button
                type="button"
                role="switch"
                aria-checked={!!draft.purchaseEnabled}
                onClick={() => setDraft({ ...draft, purchaseEnabled: !draft.purchaseEnabled })}
                className="relative w-11 h-6 rounded-full shrink-0 transition-colors"
                style={{ background: draft.purchaseEnabled ? accent : '#cbd5e1' }}
              >
                <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all" style={{ left: draft.purchaseEnabled ? '22px' : '2px' }} />
              </button>
            </div>
          </div>
        )}

        {warehouseEnabled && (
          <div 
            className="md:col-span-2 rounded-lg bg-white border border-slate-200 border-l-4 px-4 py-3 shadow-sm flex flex-col gap-3"
            style={{ borderLeftColor: accent }}
          >
            <p className="text-[11px] font-bold text-[#0d0d0d] flex items-center gap-1.5">
              <Warehouse size={12} aria-hidden="true" /> Source Warehouses
              {draft.zoneId && <span className="text-[10px] font-medium text-slate-400 normal-case tracking-normal">(from this branch's zone)</span>}
            </p>

            {!draft.zoneId ? (
              <p className="text-[10px] font-medium text-slate-400">Pick a zone to see its warehouses.</p>
            ) : zoneWarehouses.length === 0 ? (
              <p className="text-[10px] font-medium text-slate-400">No warehouses in this zone yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {zoneWarehouses.map((w) => {
                  const checked = (draft.warehouseSourceIds || []).includes(w.id);
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => toggleWarehouse(w.id)}
                      aria-pressed={checked}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 border text-left transition-colors"
                      style={{
                        borderColor: checked ? accent : 'var(--theme-tertiary)',
                        background: checked ? 'var(--theme-primary-soft)' : '#fff',
                      }}
                    >
                      <span
                        className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: checked ? accent : 'transparent', color: checked ? '#fff' : '#94a3b8', border: checked ? 'none' : '1px solid var(--theme-tertiary)' }}
                        aria-hidden="true"
                      >
                        {checked && <Check size={12} />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold text-[#0d0d0d] truncate">{w.name}</p>
                        <p className="text-[10px] font-medium text-slate-400 truncate">{w.address || '—'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Cross-zone (custom) selections already on the branch */}
            {customSelected.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Cross-zone warehouses</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {customSelected.map((w) => {
                    const z = findZone(w.zoneId);
                    return (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => toggleWarehouse(w.id)}
                        aria-pressed
                        className="flex items-center gap-2 rounded-lg px-3 py-2 border text-left transition-colors"
                        style={{ borderColor: accent, background: 'var(--theme-primary-soft)' }}
                      >
                        <span className="w-5 h-5 rounded-md flex items-center justify-center shrink-0" style={{ background: accent, color: '#fff' }} aria-hidden="true">
                          <Check size={12} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-[#0d0d0d] truncate">{w.name}</p>
                          <p className="text-[10px] font-medium text-slate-400 truncate">Zone · {z?.name || '—'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add-custom panel — pick warehouses outside this branch's zone */}
            {otherZoneWarehouses.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setCustomOpen((v) => !v)}
                  aria-expanded={customOpen}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-[0.14em] transition-colors"
                  style={{ borderColor: 'var(--theme-tertiary)', color: customOpen ? accent : '#475569' }}
                >
                  <Plus size={12} aria-hidden="true" /> Add custom
                  {customOpen ? <ChevronUp size={12} aria-hidden="true" /> : <ChevronDown size={12} aria-hidden="true" />}
                </button>
                {customOpen && (
                  <div className="mt-2 rounded-lg border border-dashed border-[var(--theme-tertiary)] p-3 flex flex-col gap-3">
                    <p className="text-[10px] font-medium text-slate-500">
                      Warehouses outside this branch's zone — useful for cross-border supply (e.g. a Pondicherry warehouse feeding a Tamil Nadu branch).
                    </p>
                    {otherByZone.length === 0 && (
                      <p className="text-[10px] font-medium text-slate-400 italic">
                        All cross-zone warehouses are already attached. Untap a chip above to release one.
                      </p>
                    )}
                    {otherByZone.map(({ zone, warehouses: whs }) => (
                      <div key={zone?.id || 'nozone'}>
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">
                          Zone · {zone?.name || 'Unassigned'}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {whs.map((w) => {
                            const checked = (draft.warehouseSourceIds || []).includes(w.id);
                            return (
                              <button
                                key={w.id}
                                type="button"
                                onClick={() => toggleWarehouse(w.id)}
                                aria-pressed={checked}
                                className="flex items-center gap-2 rounded-lg px-3 py-2 border text-left transition-colors"
                                style={{
                                  borderColor: checked ? accent : 'var(--theme-tertiary)',
                                  background: checked ? 'var(--theme-primary-soft)' : '#fff',
                                }}
                              >
                                <span
                                  className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                                  style={{ background: checked ? accent : 'transparent', color: checked ? '#fff' : '#94a3b8', border: checked ? 'none' : '1px solid var(--theme-tertiary)' }}
                                  aria-hidden="true"
                                >
                                  {checked && <Check size={12} />}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-[11px] font-bold text-[#0d0d0d] truncate">{w.name}</p>
                                  <p className="text-[10px] font-medium text-slate-400 truncate">{w.address || '—'}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="text-[10px] font-medium text-slate-400">Stock from these warehouses flows into this branch's inventory.</p>
          </div>
        )}
      </div>

      <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-[11px] font-bold uppercase tracking-[0.14em]"
          style={{ background: accent }}
        >
          {mode === 'edit' ? 'Save branch' : 'Create branch'}
        </button>
      </footer>
    </SideDrawer>
  );
};

BranchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  zones: PropTypes.array.isRequired,
  zoneEnabled: PropTypes.bool.isRequired,
  warehouseEnabled: PropTypes.bool.isRequired,
  showNotification: PropTypes.func,
};

export const BranchesTab = ({ org, showNotification }) => {
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);

  const zoneEnabled = isModuleOn(org, 'zone');
  const warehouseEnabled = isModuleOn(org, 'warehouse');
  const zones = useMemo(() => zonesOf(org.id), [org.id, version]);

  const list = useMemo(() => {
    const all = branchesOf(org.id);
    const filtered = zoneFilter === 'all' ? all : all.filter((b) => b.zoneId === zoneFilter);
    if (!search.trim()) return filtered;
    const q = search.toLowerCase();
    return filtered.filter((b) =>
      (b.name || '').toLowerCase().includes(q));
  }, [search, zoneFilter, org.id, version]);

  const accent = org.theme.primary;

  const openCreate = () => {
    setDraft(blank(org.id, zones[0]?.id));
    setModalMode('create');
    setModalOpen(true);
  };
  const openEdit = (b) => { setDraft({ ...b, warehouseSourceIds: [...(b.warehouseSourceIds || [])] }); setModalMode('edit'); setModalOpen(true); };

  const submit = (record) => {
    if (modalMode === 'create') {
      addBranch(record);
      if (showNotification) showNotification(`Branch "${record.name}" created with its own inventory.`, 'success');
    } else {
      updateBranch(record.id, record);
      if (showNotification) showNotification('Branch updated.', 'success');
    }
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    const ok = deleteBranch(confirmDelete.id);
    if (showNotification) {
      showNotification(ok ? 'Branch removed.' : 'The Main branch cannot be removed.', ok ? 'success' : 'error');
    }
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  const togglePurchase = (b) => {
    if (b.isMain) return; // Main branch always has purchasing enabled
    setBranchPurchaseEnabled(b.id, !b.purchaseEnabled);
    if (showNotification) showNotification(`${b.name} · purchases ${!b.purchaseEnabled ? 'enabled' : 'disabled'}.`, 'success');
    setVersion((v) => v + 1);
  };

  const toggleStatus = (b) => {
    updateBranch(b.id, { status: b.status === 'Active' ? 'Inactive' : 'Active' });
    if (showNotification) showNotification(`Branch "${b.name}" marked as ${b.status === 'Active' ? 'Inactive' : 'Active'}.`, 'success');
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4 h-fit">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search branches…"
              aria-label="Search branches"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
          {zoneEnabled && (
            <CustomSelect
              value={zoneFilter}
              onChange={(v) => setZoneFilter(v)}
              className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-3 py-2 text-xs font-bold text-[#0d0d0d] outline-none"
              options={[{ value: 'all', label: 'All zones' }, ...zones.map((z) => ({ value: z.id, label: z.name }))]}
            />
          )}
        </div>
        <button
          type="button"
          onClick={openCreate}
          disabled={zoneEnabled && zones.length === 0}
          title={(zoneEnabled && zones.length === 0) ? 'Create a zone first' : undefined}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-[11px] font-bold uppercase tracking-[0.14em] transition-colors shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
        >
          <Plus size={14} aria-hidden="true" /> New branch
        </button>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg h-fit">
        <table className="w-full text-left border-collapse">
          <thead>
              <tr className="border-b border-[var(--theme-tertiary)] bg-gray-50/50">
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Branch Name</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Mobile</th>
                {zoneEnabled && <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[10%]">Zone</th>}
                {warehouseEnabled && <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Warehouse</th>}
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[12%]">Inventory</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[13%]">Status</th>
                <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[15%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((b) => {
                const zone = b.zoneId ? findZone(b.zoneId) : null;
                const warehouses = (b.warehouseSourceIds || []).map(id => findWarehouse(id)).filter(Boolean);
                const warehouseNames = warehouses.length > 0 ? warehouses.map(w => w.name).join(', ') : '—';
                return (
                  <tr key={b.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex flex-col items-start justify-center">
                        {b.isMain && (
                          <span className="text-[10px] font-bold uppercase tracking-[0.1em] mb-0.5" style={{ color: accent }}>
                            Main
                          </span>
                        )}
                        <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-700">{b.name}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[12px] font-medium text-slate-700">{b.phone || '—'}</p>
                    </td>
                    {zoneEnabled && (
                      <td className="px-5 py-3">
                        <p className="text-[12px] font-medium text-slate-700">{zone ? zone.name : '—'}</p>
                      </td>
                    )}
                    {warehouseEnabled && (
                      <td className="px-5 py-3">
                        <p className="text-[12px] font-medium text-slate-700 truncate max-w-[150px]" title={warehouseNames}>
                          {warehouseNames}
                        </p>
                      </td>
                    )}
                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-slate-700">
                        1 Active
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatusToggle
                        on={(b.status || 'Active') === 'Active'}
                        label={b.name}
                        onChange={() => toggleStatus(b)}
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${b.name}`} title="Edit" onClick={() => openEdit(b)} />
                        <IconAction
                          icon={Trash2}
                          tone="danger"
                          label={`Remove ${b.name}`}
                          title={b.isMain ? 'The Main branch cannot be removed' : 'Remove'}
                          disabled={b.isMain}
                          onClick={() => setConfirmDelete(b)}
                        />
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan={zoneEnabled ? 7 : 6} className="px-5 py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                    No branches match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      <BranchModal
        open={modalOpen}
        draft={draft}
        setDraft={setDraft}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        mode={modalMode}
        accent={accent}
        zones={zones}
        zoneEnabled={zoneEnabled}
        warehouseEnabled={warehouseEnabled}
        showNotification={showNotification}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">This branch and its inventory association will be removed.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-[11px] font-bold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

BranchesTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
